import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, ComunidadNewsletterEdition } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { RichTextEditor } from "./RichTextEditor";
import { RichContent } from "./RichContent";
import { hasVocesBlock, htmlToPlainText, isRichHtml } from "../richText";
import {
  clearLocalDraft,
  hasContent,
  readLocalDraft,
  saveLocalDraft,
  useLocalDraft,
  useWarnBeforeLeaving,
} from "../useLocalDraft";

const EMPTY_FORM = { month: "", theme: "", body: "", voicesQuestion: "" };

/** Cómo se va a ver la edición desplegada en Comunidad (mismo componente que la pantalla). */
const NewsletterPreview = ({
  month,
  theme,
  body,
  voicesQuestion,
}: {
  month: string;
  theme: string;
  body: string;
  voicesQuestion: string;
}) => (
  <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[#C9BCE8] bg-white p-4">
    <span className="text-[11px] font-bold uppercase tracking-wide text-[#8475B3]">Vista previa</span>
    {month && (
      <span className="w-fit rounded-full bg-[#EDE6FA] px-3 py-0.5 text-xs font-semibold text-[#4A3D73]">{month}</span>
    )}
    {theme && <span className="text-xl font-extrabold text-[#2D2049]">{theme}</span>}
    {body ? (
      <RichContent html={body} renderVoces={() => <VocesPreview question={voicesQuestion} />} />
    ) : (
      <span className="text-sm text-gray-400">Todavía no hay contenido.</span>
    )}
    {/* Sin el bloque ubicado en el editor, el renglón va al final de la edición. */}
    {voicesQuestion.trim() && !hasVocesBlock(body) && <VocesPreview question={voicesQuestion} />}
  </div>
);

/** Cómo se ve el renglón de "Voces de la Red" (sin poder responder: es la vista previa). */
const VocesPreview = ({ question }: { question: string }) => (
  <div className="flex flex-col gap-1.5 rounded-xl bg-[#F5F1FF] p-4">
    <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Voces de la Red</span>
    {question.trim() ? (
      <span className="text-[15px] font-semibold text-[#2D2049]">{question}</span>
    ) : (
      <span className="text-[13px] font-semibold text-[#C46A1A]">
        Falta escribir la pregunta en el campo de abajo del editor.
      </span>
    )}
    <span className="rounded-lg border border-[#E3D9FF] bg-white px-3 py-2 text-[13px] text-gray-400">
      Acá cada psicóloga escribe su respuesta…
    </span>
  </div>
);

/** Pregunta de "Voces de la Red" — opcional; cierra la edición y se responde desde Comunidad. */
const VoicesQuestionInput = ({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) => (
  <div className="flex flex-col gap-1">
    <label htmlFor={id} className="text-xs font-semibold text-[#665090]">
      Pregunta de &ldquo;Voces de la Red&rdquo; (opcional)
    </label>
    <input
      id={id}
      className="rounded-lg border border-[#C9BCE8] px-3 py-2 text-sm"
      placeholder="Ej: ¿Qué hacés para cortar al final del día?"
      maxLength={500}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <span className="text-[11px] text-gray-400">
      Para elegir dónde aparece el renglón para responder, tocá «Voces de la Red» en la barra del editor
      y ubicalo en el texto (si no, va al final). Se puede responder hasta que publiques la edición siguiente.
    </span>
  </div>
);

/** Editor del archivo de ediciones del newsletter (borrador) — cada edición tiene un mes,
 *  un tema breve (lo que se ve plegado en el archivo) y el contenido completo. */
export const NewsletterEditionsEditor = () => {
  const [editions, setEditions] = useState<ComunidadNewsletterEdition[]>([]);
  const [loading, setLoading] = useState(true);
  // Lo que se está escribiendo se respalda en este navegador mientras se escribe: si se
  // recarga la página o se publica antes de "Agregar edición", no se pierde (ver useLocalDraft).
  const [newForm, setNewForm, clearNewForm] = useLocalDraft("newsletter-new", EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState(EMPTY_FORM);
  const editDraftKey = editingId ? `newsletter-edit-${editingId}` : null;
  // Lo que tenía la edición al abrirla: si no se cambió nada, no hay nada que respaldar.
  const [editingOriginal, setEditingOriginal] = useState("");
  useEffect(() => {
    if (!editDraftKey) return;
    if (JSON.stringify(editingForm) === editingOriginal) clearLocalDraft(editDraftKey);
    else saveLocalDraft(editDraftKey, editingForm);
  }, [editDraftKey, editingForm, editingOriginal]);
  useWarnBeforeLeaving(hasContent(newForm) || (!!editingId && JSON.stringify(editingForm) !== editingOriginal));
  // "Vista previa": cómo la va a ver la psicóloga, antes de guardar.
  const [previewNew, setPreviewNew] = useState(false);
  const [previewEdit, setPreviewEdit] = useState(false);
  const plain = (body: string) => (isRichHtml(body) ? htmlToPlainText(body) : body);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await comunidadAdapter.listDraftNewsletterEditions();
      setEditions(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleAdd = async () => {
    const { month, theme, body, voicesQuestion } = newForm;
    if (!month.trim() || !theme.trim() || !body.trim()) {
      toastUtils.error("Completá mes, tema y contenido para la nueva edición");
      return;
    }
    try {
      await comunidadAdapter.createNewsletterEdition(month.trim(), theme.trim(), body.trim(), voicesQuestion.trim());
    } catch {
      toastUtils.error("No se pudo agregar la edición. Lo que escribiste sigue acá: probá de nuevo.");
      return;
    }
    clearNewForm();
    toastUtils.success("Edición agregada al borrador. Para que la vean, tocá «Publicar cambios».");
    reload();
  };

  const startEdit = (edition: ComunidadNewsletterEdition) => {
    const fromServer = {
      month: edition.month,
      theme: edition.theme,
      body: edition.body,
      voicesQuestion: edition.voicesQuestion ?? "",
    };
    setEditingOriginal(JSON.stringify(fromServer));
    setEditingId(edition.id);
    // Si quedaron cambios sin guardar de esta edición (recarga, publicar a mitad), se recuperan.
    const pending = readLocalDraft<typeof EMPTY_FORM>(`newsletter-edit-${edition.id}`);
    if (pending && hasContent(pending)) {
      setEditingForm({ ...EMPTY_FORM, ...pending });
      toastUtils.info("Recuperamos los cambios que no habías guardado de esta edición.");
      return;
    }
    setEditingForm(fromServer);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const { month, theme, body, voicesQuestion } = editingForm;
    if (!month.trim() || !theme.trim() || !body.trim()) {
      toastUtils.error("Completá mes, tema y contenido");
      return;
    }
    try {
      await comunidadAdapter.updateNewsletterEdition(editingId, month.trim(), theme.trim(), body.trim(), voicesQuestion.trim());
    } catch {
      toastUtils.error("No se pudo guardar. Lo que escribiste sigue acá: probá de nuevo.");
      return;
    }
    clearLocalDraft(`newsletter-edit-${editingId}`);
    setEditingId(null);
    reload();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar esta edición del borrador?")) return;
    await comunidadAdapter.deleteNewsletterEdition(id);
    reload();
  };

  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <h3 className="m-0 text-base font-bold text-[#2D2049]">Archivo del newsletter</h3>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress /></div>
      ) : (
        <div className="flex flex-col gap-2">
          {editions.length === 0 && (
            <p className="m-0 text-sm text-gray-400">Sin ediciones en el borrador todavía.</p>
          )}
          {editions.map((edition) => (
            <div key={edition.id} className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] px-3 py-2.5">
              {editingId === edition.id ? (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <input
                      className="w-1/2 rounded-lg border border-[#C9BCE8] px-2 py-1 text-sm"
                      placeholder="Mes (ej. octubre)"
                      value={editingForm.month}
                      onChange={(e) => setEditingForm((f) => ({ ...f, month: e.target.value }))}
                    />
                    <input
                      className="w-1/2 rounded-lg border border-[#C9BCE8] px-2 py-1 text-sm"
                      placeholder="Tema breve"
                      value={editingForm.theme}
                      onChange={(e) => setEditingForm((f) => ({ ...f, theme: e.target.value }))}
                    />
                  </div>
                  <RichTextEditor
                    idPrefix={`newsletter-edit-${edition.id}`}
                    placeholder="Contenido completo de la edición"
                    value={editingForm.body}
                    onChange={(body) => setEditingForm((f) => ({ ...f, body }))}
                    allowVoces
                  />
                  <VoicesQuestionInput
                    id={`newsletter-edit-${edition.id}-voices`}
                    value={editingForm.voicesQuestion}
                    onChange={(voicesQuestion) => setEditingForm((f) => ({ ...f, voicesQuestion }))}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="self-start border-[#C9BCE8] text-[#665090]"
                    onClick={() => setPreviewEdit((v) => !v)}
                  >
                    {previewEdit ? "Ocultar vista previa" : "Vista previa"}
                  </Button>
                  {previewEdit && (
                    <NewsletterPreview
                      month={editingForm.month}
                      theme={editingForm.theme}
                      body={editingForm.body}
                      voicesQuestion={editingForm.voicesQuestion}
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-[#2D2049]">
                    Edición #{edition.editionNumber} — {edition.month}: {edition.theme}
                  </span>
                  <span className="line-clamp-2 text-xs text-gray-500">{plain(edition.body)}</span>
                  {edition.voicesQuestion && (
                    <span className="text-xs text-[#665090]">💬 Voces de la Red: {edition.voicesQuestion}</span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 self-end">
                {editingId === edition.id ? (
                  <Button size="sm" onClick={saveEdit}>Guardar</Button>
                ) : (
                  <Button size="sm" variant="outline" className="border-[#C9BCE8] text-[#665090]" onClick={() => startEdit(edition)}>
                    Editar
                  </Button>
                )}
                <button type="button" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(edition.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2">
        <div className="flex gap-2">
          <input
            className="w-1/2 rounded-lg border border-[#E3D9FF] px-3 py-2 text-sm"
            placeholder="Mes (ej. octubre)"
            value={newForm.month}
            onChange={(e) => setNewForm((f) => ({ ...f, month: e.target.value }))}
          />
          <input
            className="w-1/2 rounded-lg border border-[#E3D9FF] px-3 py-2 text-sm"
            placeholder="Tema breve"
            value={newForm.theme}
            onChange={(e) => setNewForm((f) => ({ ...f, theme: e.target.value }))}
          />
        </div>
        <RichTextEditor
          idPrefix="newsletter-new"
          placeholder="Contenido completo de la nueva edición…"
          value={newForm.body}
          onChange={(body) => setNewForm((f) => ({ ...f, body }))}
          allowVoces
        />
        <VoicesQuestionInput
          id="newsletter-new-voices"
          value={newForm.voicesQuestion}
          onChange={(voicesQuestion) => setNewForm((f) => ({ ...f, voicesQuestion }))}
        />
        <Button
          size="sm"
          variant="outline"
          className="self-start border-[#C9BCE8] text-[#665090]"
          onClick={() => setPreviewNew((v) => !v)}
        >
          {previewNew ? "Ocultar vista previa" : "Vista previa"}
        </Button>
        {previewNew && (
          <NewsletterPreview
            month={newForm.month}
            theme={newForm.theme}
            body={newForm.body}
            voicesQuestion={newForm.voicesQuestion}
          />
        )}
        <Button size="sm" className="self-start" onClick={handleAdd}>
          <Plus size={15} className="mr-1" />
          Agregar edición
        </Button>
      </div>
    </div>
  );
};
